import { useQueryClient } from "@tanstack/react-query";
import { logger } from "@artis/logger";
import { useCallback, useEffect, useRef } from "react";

import { useGraphQLClient } from "@package/graphql/react";
import { useCalcWorker } from "../../webapp/calcs/react/hooks";
import { useAuth0 } from "@auth0/auth0-react";
import { sourcesQueryKey, type ProductInfo } from "../../data";
import type { TProductConfigs } from "../../data/cached";
import { useProductConfigsCache } from "../../data/cached/preload";
import { permissionRowsFingerprint, usePermissionRowsSub } from "../../data/user";
import { useUserId } from "./hooks";

export const RefreshAccessToken = () => {
  const graphql = useGraphQLClient();
  const qry = useQueryClient();
  const userId = useUserId();
  const worker = useCalcWorker();
  const cache = useProductConfigsCache();
  const { getAccessTokenSilently } = useAuth0();
  const permissions = usePermissionRowsSub(userId);

  // Refetches the access token and product info. If any products are in the current cache but not in the refreshed info, then it implies the user lost permissions for those products, so we remove them from the cache and calcs.
  const refreshTokenAndProducts = useCallback(async () => {
    const token = await getAccessTokenSilently({
      detailedResponse: true,
      cacheMode: "off",
      authorizationParams: { audience: "hasura" },
    });
    worker.send({ type: "access-token", data: token.access_token });
    qry.setQueryData(["auth", userId, "access_token"], token);

    void qry.invalidateQueries({ queryKey: sourcesQueryKey });
    // Folder permissions decide which packages Curve Management shows and which
    // of them count as globalised. Both queries are cached for the session
    // (`curvePackages` inherits the client's infinite staleTime), so without
    // this a permission change only lands on the next full reload.
    qry.invalidateQueries({ queryKey: ["curvePackages", userId] });
    qry.invalidateQueries({ queryKey: ["globalisedPackageIds", userId] });

    const oldInfo = cache.value().info;
    const refreshed = await cache.refresh(graphql);
    if (!refreshed) return;

    const directlyRemoved = new Set<string>();
    if (oldInfo && refreshed.info) {
      for (const id of Object.keys(oldInfo)) {
        if (!(id in refreshed.info)) directlyRemoved.add(id);
      }
    }

    if (refreshed.info) qry.setQueryData<ProductInfo>(["products", userId], refreshed.info);
    if (refreshed.configs) qry.setQueryData<TProductConfigs>(["configs", userId], refreshed.configs);

    if (directlyRemoved.size > 0) {
      worker.grid.applyUpdate({
        type: "grid:update:root",
        data: { update: [], remove: [...directlyRemoved] },
      });
    }
  }, [cache, graphql, qry, userId, worker, getAccessTokenSilently]);

  // Runs on boot to catch any permission changes that were flagged by the in-session handler, but were not fully handled before the user refreshed the page. Basically prevents the user from being able to refresh within a ~10 second window after their permissions have updated in Hasura but their Auth0 token still has the old permissions.
  useEffect(() => {
    if (!safeHasFlag()) return;
    let cancelled = false;
    (async () => {
      await new Promise((resolve) => setTimeout(resolve, 10_000));
      if (cancelled) return;
      try {
        await refreshTokenAndProducts();
      } catch (err) {
        logger.error("Boot-time permission refresh failed", err);
      } finally {
        safeClearFlag();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshTokenAndProducts]);

  const scheduleRefreshRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    scheduleRefreshRef.current = () => {
      logger.info("User permissions updated");
      // Set the flag immediately so a page refresh during the 15s wait below still triggers a boot-time refetch on the next mount.
      safeSetFlag();

      // Wait 15s before refreshing the token — the subscription can fire before Auth0 has propagated the new claims. A newer update or unmount cancels the pending wait; the newer update reschedules.
      if (timeoutId !== null) clearTimeout(timeoutId);
      timeoutId = setTimeout(async () => {
        timeoutId = null;
        if (cancelled) return;
        try {
          await refreshTokenAndProducts();
        } finally {
          safeClearFlag();
        }
      }, 15_000);
    };

    return () => {
      cancelled = true;
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      scheduleRefreshRef.current = null;
    };
  }, [refreshTokenAndProducts]);

  const onFolderPermissionsChanged = useCallback(() => scheduleRefreshRef.current?.(), []);

  useEffect(() => {
    if (permissions.error) logger.warn("Folder permissions subscription failed", { error: permissions.error });
  }, [permissions.error]);

  const permissionRows = permissions.data?.permission;
  useFingerprintChange(
    userId,
    permissionRows ? permissionRowsFingerprint(permissionRows) : null,
    onFolderPermissionsChanged,
  );

  return null;
};

const useFingerprintChange = (userId: string | undefined, fingerprint: string | null, onChange: () => void) => {
  const last = useRef<{ userId: string | undefined; fingerprint: string } | null>(null);

  useEffect(() => {
    if (fingerprint === null) return;
    const previous = last.current;
    last.current = { userId, fingerprint };
    if (!previous || previous.userId !== userId) return;
    if (previous.fingerprint !== fingerprint) onChange();
  }, [userId, fingerprint, onChange]);
};

const PERMISSION_CHANGED_KEY = "permission-changed-refresh";

const safeSetFlag = () => {
  try {
    logger.info("Setting permission change flag in localStorage");
    localStorage.setItem(PERMISSION_CHANGED_KEY, "1");
  } catch {}
};
const safeClearFlag = () => {
  try {
    logger.info("Clearing permission change flag from localStorage");
    localStorage.removeItem(PERMISSION_CHANGED_KEY);
  } catch {}
};
const safeHasFlag = () => {
  try {
    return !!localStorage.getItem(PERMISSION_CHANGED_KEY);
  } catch {
    return false;
  }
};
